/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { flatMap } from 'lodash'

import { EuiSpacer } from '@elastic/eui'

import { CuiTable } from '@modules/cui/Table'
import type { Props as CuiTableProps, PaginationProps } from '@modules/cui/Table/types'
import type { Invoice, Statement } from '@modules/billing-lib/types/history'

import { getFullYear } from '../lib/date'

import TablePagination from './TablePagination'

import type { FunctionComponent } from 'react'

type Document = Statement | Invoice

export type Props = CuiTableProps<Document> & {
  documents: Document[]
}

const BillingDocuments: FunctionComponent<Props> = ({ documents, ...props }) => {
  const [filterByYear, setFilterByYear] = useState<number | undefined>(undefined)

  const getDocumentYears = ({ period_start_date, period_end_date }: Document): number[] => {
    const startYear = getFullYear(period_start_date)
    const endYear = getFullYear(period_end_date)

    return [startYear, endYear]
  }

  const getSortedBillingYears = (): number[] =>
    [...new Set<number>(flatMap(documents, (document) => getDocumentYears(document)))].sort(
      (a, b) => a - b,
    )

  const getGridRows = (): Document[] => {
    if (!documents.length) {
      return []
    }

    if (!filterByYear) {
      return documents
    }

    return documents.filter((statement) => {
      const [startYear, endYear] = getDocumentYears(statement)
      return filterByYear === startYear || filterByYear === endYear
    })
  }

  return (
    <CuiTable
      pageSize={5}
      rows={getGridRows()}
      renderPagination={({ activePage, pageCount, onChangePage }: PaginationProps) => (
        <Fragment>
          <EuiSpacer size='m' />
          <TablePagination
            billingYears={getSortedBillingYears()}
            activePage={activePage}
            pageCount={pageCount}
            onChangePage={onChangePage}
            onFilterByYear={setFilterByYear}
          />
        </Fragment>
      )}
      {...props}
    />
  )
}

export default BillingDocuments
